import { useEffect } from "react";
import { Plus } from "lucide-react";
import Link from "../shims/link";
import { useScreen } from "../use-screen";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TripStatusBadge } from "@/components/trips/trip-status-badge";
import { prefetchFromTripList } from "../prefetch-routes";
import type { Trip } from "@/server/trips";

/**
 * 내 여행 목록. Next `(app)/trips/page.tsx`의 마크업을 그대로 쓰고 데이터만
 * `GET /api/trips`에서 받는다. `Trip` 타입도 서버 모듈에서 그대로 가져온다 —
 * 응답이 그 모양이라 따로 정의하면 두 벌이 되어 어긋난다.
 */
export default function TripsPage() {
  // 한가할 때 다음에 갈 만한 화면을 미리 받아 둔다(prefetch-routes.ts).
  useEffect(() => prefetchFromTripList(), []);
  const { data, error, loading } = useScreen<{ trips: Trip[] }>("/api/trips");

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">내 여행</h1>
        <Button asChild>
          <Link href="/trips/new">
            <Plus /> 새 여행
          </Link>
        </Button>
      </div>

      {error && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">불러오는 중…</p>
      ) : (data?.trips.length ?? 0) === 0 ? (
        <Card className="items-center gap-4 py-10 text-center">
          <CardHeader className="w-full items-center text-center">
            <CardTitle>아직 여행이 없어요</CardTitle>
            <CardDescription>새 여행을 만들어 일정을 계획해보세요.</CardDescription>
          </CardHeader>
          <Button asChild>
            <Link href="/trips/new">
              <Plus /> 새 여행 만들기
            </Link>
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data!.trips.map((trip) => (
            <TripCard key={trip.id} trip={trip} />
          ))}
        </div>
      )}
    </div>
  );
}

function TripCard({ trip }: { trip: Trip }) {
  return (
    <Link href={`/trips/${trip.id}`} className="group block h-full">
      <Card className="h-full border border-border/80 shadow-xs hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-200">
        <CardHeader className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="group-hover:text-primary transition-colors text-base font-semibold">
              {trip.title}
            </CardTitle>
            <TripStatusBadge startDate={trip.startDate} endDate={trip.endDate} />
          </div>
          <CardDescription>
            {trip.destination ? `${trip.destination} · ` : ""}
            {trip.startDate} ~ {trip.endDate}
          </CardDescription>
        </CardHeader>
      </Card>
    </Link>
  );
}
